import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import DownloadIcon from 'components/SVG/DownloadIcon';
import { useAddTranscription } from 'hooks/useAddTranscription';
import { useTranscriptionByFileID } from 'hooks/useTranscriptionByFileID';
import { useTranscriptionQueueJob } from 'hooks/useTranscriptionQueueJob';
import { InfoCard, SparkAccordion, SparkButton } from 'sharedComponents/UI';
import { useWorkspaceStore } from 'stores';
import { parseJSONCleaned } from 'utilities/index';

type BoardNodeTranscriptionProps = { fileID: string };

function BoardNodeTranscription({ fileID }: BoardNodeTranscriptionProps) {
  const { transcription, isLoading: isTranscriptionLoading } =
    useTranscriptionByFileID(fileID);
  const { mutate: createTranscription, isLoading: isAddingTranscription } =
    useAddTranscription();
  const { workspace } = useWorkspaceStore();
  const { isRunning, isError } = useTranscriptionQueueJob(transcription?.id);

  const isCreatingTranscription = isAddingTranscription || isRunning;

  const handleCreateTranscription = () => {
    createTranscription({ fileID, workspaceID: workspace?.id });
  };

  if (!fileID) {
    // if we don't have a fileID, we don't need to show the transcription
    return null;
  }

  if (isTranscriptionLoading) {
    return (
      <Box
        marginTop={2}
        display='flex'
        gap={2}
        border={(theme) => `1px solid ${theme.palette.borderColor}`}
        borderRadius='10px'
        padding={2}
        justifyContent='space-between'
      >
        <Skeleton variant='text' width='10rem' height='100%' />
        <Box display='flex' gap={2}>
          <Skeleton variant='text' width='1rem' height='100%' />
          <Skeleton variant='text' width='1rem' height='100%' />
        </Box>
      </Box>
    );
  }

  const isTranscriptionAvailable = transcription?.meta?.data;

  if (!isTranscriptionAvailable) {
    return (
      <Box marginTop={2}>
        <InfoCard
          info='No transcription found'
          cardStyles={{
            maxHeight: 'unset',
          }}
        >
          <SparkButton
            variant='contained'
            onClick={handleCreateTranscription}
            isLoading={isCreatingTranscription}
          >
            Create
          </SparkButton>
        </InfoCard>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box marginTop={2}>
        <InfoCard
          info='Error fetching transcription'
          type='error'
          cardStyles={{
            maxHeight: 'unset',
          }}
        >
          <SparkButton
            onClick={handleCreateTranscription}
            isLoading={isCreatingTranscription}
          >
            Retry
          </SparkButton>
        </InfoCard>
      </Box>
    );
  }

  const data = transcription?.meta.data;

  const transcriptionData = parseJSONCleaned<any>(data);

  return (
    <SparkAccordion
      sx={{
        bgcolor: (theme) => theme.palette.primary.light,
        marginTop: 2,
      }}
      title='Transcription'
      detailsStyle={{
        padding: 1,
      }}
      secondaryIcon={<DownloadIcon color='black' width='24' />}
      onSecondaryIconClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Typography variant='body1'>{transcriptionData?.Text || ''}</Typography>
    </SparkAccordion>
  );
}

export default BoardNodeTranscription;
